import {
  AGENT_MODEL_AUTO,
  normalizeAgentModelSelection,
} from '@tabbio/utils/agent-models';
import {
  persistPreference,
  readPersistedPreference,
} from './app-preferences-storage';

export const AGENT_MODEL_STORAGE_KEY = 'tabbio-agent-model';

let currentSnapshot: string | null = null;

/**
 * The composer's model choice is a sticky *global* preference, not per-thread:
 * switching models mid-conversation is what ChatGPT and Claude.ai do, and it
 * keeps the picker's state from surprising the user when they open an old
 * thread.
 *
 * Both platforms persist it — `localStorage` on web, SecureStore on native —
 * so the pick survives a relaunch on the phone the same way it survives a
 * reload in the browser.
 */
export function readAgentModelPreference(): string {
  if (currentSnapshot !== null) return currentSnapshot;
  const stored = readPersistedPreference(AGENT_MODEL_STORAGE_KEY);
  // A stored id that has since left the catalog (entry removed, or the user
  // downgraded) must not wedge chat — fall back to Auto.
  currentSnapshot = normalizeAgentModelSelection(stored) ?? AGENT_MODEL_AUTO;
  return currentSnapshot;
}

export function writeAgentModelPreference(id: string) {
  persistPreference(
    AGENT_MODEL_STORAGE_KEY,
    normalizeAgentModelSelection(id) ?? AGENT_MODEL_AUTO,
  );
}

type Listener = (id: string) => void;

const listeners = new Set<Listener>();

/**
 * Both composers (hero + tray) can be mounted at once, and only one of them is
 * ever the one the user clicked. A tiny pub/sub keeps them in sync without
 * lifting state into a provider that only these two components would use.
 */
export function subscribeAgentModelPreference(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setAgentModelPreference(id: string) {
  const next = normalizeAgentModelSelection(id) ?? AGENT_MODEL_AUTO;
  // SecureStore writes are asynchronous. Keep the external-store snapshot in
  // memory first so the native trigger updates in the same frame as the tap.
  currentSnapshot = next;
  writeAgentModelPreference(next);
  for (const listener of listeners) listener(next);
}

export function resetAgentModelPreferenceCacheForTests() {
  currentSnapshot = null;
}
